import React, { useEffect, useMemo, useState } from 'react';
import { X, Save } from 'lucide-react';
import { Post, ArtCategory } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { postsService } from '../services/postsService';

interface EditPostModalProps {
  isOpen: boolean;
  post: Post;
  onClose: () => void;
  onSaved?: (updated: Post) => void;
}

const EditPostModal: React.FC<EditPostModalProps> = ({ isOpen, post, onClose, onSaved }) => {
  const { select, t } = useLanguage();
  const [title, setTitle] = useState(post.title || '');
  const [description, setDescription] = useState(post.description || '');
  const [category, setCategory] = useState<ArtCategory>(post.category);
  const [tags, setTags] = useState<string>((post.tags || []).join(', '));
  const [forSale, setForSale] = useState<boolean>(!!post.for_sale);
  const [price, setPrice] = useState<number>(post.price || 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!isOpen) return;
    setTitle(post.title || '');
    setDescription(post.description || '');
    setCategory(post.category);
    setTags((post.tags || []).join(', '));
    setForSale(!!post.for_sale);
    setPrice(post.price || 0);
    setError('');
  }, [isOpen, post]);

  const categoryLabels = useMemo<Record<ArtCategory, string>>(
    () => ({
      music: t('categories.music', select({ es: 'Música', en: 'Music' })),
      writing: t('categories.writing', select({ es: 'Escritura', en: 'Writing' })),
      tattoo: t('categories.tattoo', select({ es: 'Tatuajes', en: 'Tattoos' })),
      digital_design: t('categories.digital_design', select({ es: 'Diseño Digital', en: 'Digital Design' })),
      fashion_design: t('categories.fashion_design', select({ es: 'Diseño de Moda', en: 'Fashion Design' })),
      photography: t('categories.photography', select({ es: 'Fotografía', en: 'Photography' })),
      sculpture: t('categories.sculpture', select({ es: 'Escultura', en: 'Sculpture' })),
      painting: t('categories.painting', select({ es: 'Pintura', en: 'Painting' })),
      dance: t('categories.dance', select({ es: 'Danza', en: 'Dance' })),
      filmmaking: t('categories.filmmaking', select({ es: 'Cine', en: 'Filmmaking' })),
      makeup: t('categories.makeup', select({ es: 'Maquillaje', en: 'Makeup' })),
    }),
    [select, t]
  );

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      const updates: Partial<Post> & Record<string, unknown> = {
        title: title.trim(),
        description: description.trim(),
        category,
        tags: tags
          .split(',')
          .map((t) => t.trim())
          .filter((v) => v.length > 0),
        for_sale: forSale,
        price: forSale ? Number(price) || 0 : null,
      };
      const updated = await postsService.updatePost(post.id, updates);
      onSaved?.(updated as Post);
      onClose();
    } catch (err: unknown) {
      console.error('Error updating post:', err);
      const message =
        err instanceof Error
          ? err.message
          : select({ es: 'No se pudo actualizar la publicación.', en: 'Failed to update the post.' });
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">{select({ es: 'Editar publicación', en: 'Edit post' })}</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{select({ es: 'Título', en: 'Title' })}</label>
            <input value={title} onChange={(e)=>setTitle(e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{select({ es: 'Descripción', en: 'Description' })}</label>
            <textarea value={description} onChange={(e)=>setDescription(e.target.value)} rows={4} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{select({ es: 'Categoría', en: 'Category' })}</label>
              <select value={category} onChange={(e)=>setCategory(e.target.value as ArtCategory)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent">
                {(Object.keys(categoryLabels) as ArtCategory[]).map((key) => (
                  <option key={key} value={key}>{categoryLabels[key]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{select({ es: 'Etiquetas (separadas por comas)', en: 'Tags (comma-separated)' })}</label>
              <input value={tags} onChange={(e)=>setTags(e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <input id="for_sale" type="checkbox" checked={forSale} onChange={(e)=>setForSale(e.target.checked)} className="rounded" />
              <label htmlFor="for_sale" className="text-sm text-gray-700">{select({ es: 'Poner a la venta', en: 'Put up for sale' })}</label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{select({ es: 'Precio', en: 'Price' })}</label>
              <input type="number" min={0} step="0.01" disabled={!forSale} value={forSale ? price : 0} onChange={(e)=>setPrice(parseFloat(e.target.value)||0)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100" />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">{select({ es: 'Cancelar', en: 'Cancel' })}</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-60 flex items-center gap-2">
            <Save size={16} />
            {saving ? select({ es: 'Guardando...', en: 'Saving...' }) : select({ es: 'Guardar cambios', en: 'Save changes' })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditPostModal;

